import { clamp } from "../util/math";
import type { CameraState, Renderer } from "./renderer";

/** Floor exists only to keep pxPerWorld > 0; effectively unlimited zoom-out.
 *  Low enough to frame the whole ~250km planet+orbit system (the player ship
 *  orbits 100km off the planet centre, so the floor must clear that). */
const MIN_ZOOM = 0.02;
/** 2× the previous cap (was 48) so the camera can sit twice as close. */
const MAX_ZOOM = 96;
const DEFAULT_ZOOM = 8;

/** exp(-k·dt) lerp; ~46 ms half-time at k=15. */
const ZOOM_SMOOTH_K = 15;
const ZOOM_SNAP_EPS = 1e-3;

/** Star-sprite size and brightness scale as `(z/8)^STAR_SCALE_EXP` —
 *  0.5 = sqrt of world-zoom factor, so sprites shrink and dim to ~71%
 *  per 2× zoom-out. */
const STAR_SCALE_EXP = 0.5;

let cssPxPerCell = DEFAULT_ZOOM;
let zoomTarget = DEFAULT_ZOOM;

export function getZoom(): number {
  return cssPxPerCell;
}

export function getZoomTarget(): number {
  return zoomTarget;
}

/** Instant set (no animation). Kept for callers that need direct control. */
export function setZoom(p: number): void {
  cssPxPerCell = clamp(p, MIN_ZOOM, MAX_ZOOM);
  zoomTarget = cssPxPerCell;
}

export function setZoomTarget(p: number): void {
  zoomTarget = clamp(p, MIN_ZOOM, MAX_ZOOM);
}

/** Geometric (log-space) lerp toward zoomTarget. Call once per render frame. */
export function updateZoomAnimation(dt: number): void {
  if (cssPxPerCell === zoomTarget) return;
  const k = 1 - Math.exp(-ZOOM_SMOOTH_K * dt);
  const ln = Math.log(cssPxPerCell) + (Math.log(zoomTarget) - Math.log(cssPxPerCell)) * k;
  cssPxPerCell = Math.exp(ln);
  if (Math.abs(cssPxPerCell - zoomTarget) / zoomTarget < ZOOM_SNAP_EPS) {
    cssPxPerCell = zoomTarget;
  }
}

/** Relative to DEFAULT_ZOOM, raised to STAR_SCALE_EXP — used by starfield.wgsl
 *  to scale both sprite radius and brightness. */
export function getStarScale(): number {
  return Math.pow(cssPxPerCell / DEFAULT_ZOOM, STAR_SCALE_EXP);
}

export function makeCamera(): CameraState {
  return {
    x: 0, y: 0,
    rot: 0, worldW: 0, worldH: 0, time: 0,
    vx: 0, vy: 0, angVel: 0,
    starScale: 1,
  };
}

/** Smooth zoom needs fractional pxPerWorld; texel-snap is dropped so animated
 *  zooms don't jitter across integer thresholds. Linear atlas filtering keeps
 *  it visually acceptable. */
function pxPerWorld(dpr: number): number {
  return Math.max(0.001, (cssPxPerCell * dpr) / 8);
}

export function syncCameraToCanvas(cam: CameraState, r: Renderer): void {
  const dpr = window.devicePixelRatio || 1;
  const p = pxPerWorld(dpr);
  cam.worldW = r.pixelW / p;
  cam.worldH = r.pixelH / p;
  cam.starScale = getStarScale();
}

/** World +y at bottom (tiles.wgsl negates clip.y) → devY/world.y same sign. */
export function canvasToWorld(
  cam: CameraState,
  r: Renderer,
  cssX: number,
  cssY: number,
): { x: number; y: number } {
  const dpr = window.devicePixelRatio || 1;
  const devX = cssX * dpr;
  const devY = cssY * dpr;
  const p = pxPerWorld(dpr);
  const vx = (devX - r.pixelW / 2) / p;
  const vy = (devY - r.pixelH / 2) / p;
  const c = Math.cos(cam.rot);
  const s = Math.sin(cam.rot);
  return {
    x: cam.x + vx * c - vy * s,
    y: cam.y + vx * s + vy * c,
  };
}
